import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message } from 'antd';
import { Risk } from '../types/risk';
import riskService from '../api/riskService';

const { TextArea } = Input;
const { Option } = Select;

interface RiskFormProps {
  visible: boolean;
  risk?: Risk;
  projectId?: number;
  onCancel: () => void;
  onSuccess: () => void;
}

const RiskForm: React.FC<RiskFormProps> = ({ 
  visible, 
  risk, 
  projectId, 
  onCancel, 
  onSuccess 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (visible) {
      if (risk) {
        form.setFieldsValue(risk);
      } else {
        form.resetFields();
        if (projectId) {
          form.setFieldsValue({ project_id: projectId });
        }
      }
    }
  }, [visible, risk, form, projectId]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (risk?.id) {
        await riskService.updateRisk(risk.id, values);
        message.success('Risk updated successfully');
      } else {
        await riskService.createRisk(values);
        message.success('Risk created successfully');
      }
      onSuccess();
    } catch (error) {
      message.error(`Failed to ${risk ? 'update' : 'create'} risk`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={risk ? 'Edit Risk' : 'Create Risk'}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          probability: 'Medium',
          impact: 'Medium',
          status: 'Identified'
        }}
      >
        <Form.Item
          name="name"
          label="Risk Name"
          rules={[{ required: true, message: 'Please enter risk name' }]}
        >
          <Input placeholder="Enter risk name" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
        >
          <TextArea rows={3} placeholder="Enter risk description" />
        </Form.Item>

        {!projectId && (
          <Form.Item
            name="project_id"
            label="Project ID"
            rules={[{ required: true, message: 'Please enter project ID' }]}
          >
            <Input type="number" placeholder="Enter project ID" />
          </Form.Item>
        )}

        <Form.Item
          name="probability"
          label="Probability"
          rules={[{ required: true, message: 'Please select probability' }]}
        >
          <Select>
            <Option value="Low">Low</Option>
            <Option value="Medium">Medium</Option>
            <Option value="High">High</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="impact"
          label="Impact"
          rules={[{ required: true, message: 'Please select impact' }]}
        >
          <Select>
            <Option value="Low">Low</Option>
            <Option value="Medium">Medium</Option>
            <Option value="High">High</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: 'Please select status' }]}
        >
          <Select>
            <Option value="Identified">Identified</Option>
            <Option value="Mitigated">Mitigated</Option>
            <Option value="Occurred">Occurred</Option>
            <Option value="Closed">Closed</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="mitigation_plan"
          label="Mitigation Plan"
        >
          <TextArea rows={4} placeholder="Describe the mitigation plan" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={onCancel} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {risk ? 'Update' : 'Create'} Risk
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RiskForm;